import { Alert, Alignment, Button, FormGroup, Icon, NonIdealState } from "@blueprintjs/core";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { IEmpresa, newEmpresa } from "../../model";
import { AppStore } from "../../stores/AppStore";
import { SpedImportTxtStore } from "../../stores/sped/SpedImportTxtStore";
import { EmpresaSelect, filterEmpresa, renderEmpresa } from "../empresa/EmpresaSelect";
import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import { formatCnpj } from "../../utils";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import moment from "moment";
import { getTag } from "../utils/getStatus";
import { Pager } from "../utils/Pager";
import LoadingModal from "../loading/LoadingModal";

@observer
export class SpedPlanilhaPage extends React.Component<{}, {}> {

  obs = observable({
    importarTxts: false as boolean,
    selectEmpresa: newEmpresa() as IEmpresa,
    current_page: 1 as number,
    selectedEmpresaImport: newEmpresa() as IEmpresa,
    open_detalhes: false as boolean,
    tarefa: {} as any
  })

  constructor(props: any) {
    super(props);
  }

  /**
   * Executa ações iniciais do componente após a montagem na tela.
   *
   * Define o título da página como "Conversor SPED para planilha FIPO" por meio do `AppStore`
   * e dispara a consulta inicial da listagem utilizando `SpedImportTxtStore.handleConsulta`,
   * carregando a primeira página (`1`) sem filtro de CNPJ (string vazia).
   *
   * Este método é utilizado no ciclo de vida de componentes React baseados em classe,
   * sendo chamado automaticamente após o componente ser renderizado no DOM.
   *
   * @async
   * @function componentDidMount
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é configurar
   *   o título da tela e iniciar o carregamento inicial dos dados.
  */

  async componentDidMount() {
    AppStore.setTitle("Conversor SPED para planilha FIPO");
    SpedImportTxtStore.handleConsulta(1, "");
  }

  /**
   * Fecha o modal/fluxo de importação de arquivos TXT e reseta a empresa selecionada.
   *
   * Define `SpedImportTxtStore.obs.importarTxts` como `false` para ocultar a interface
   * de importação e recria o estado da empresa selecionada por meio de `newEmpresa()`,
   * limpando os dados previamente preenchidos em `this.obs.selectedEmpresaImport`.
   *
   * Apesar do nome do método mencionar "Xmls", o fluxo implementado atua sobre a
   * importação de arquivos TXT (`importarTxts`).
   *
   * @function onCloseImportarXmls
   * @returns {void} Não retorna valor. O efeito esperado é fechar a importação de TXT
   *   e resetar os dados da empresa selecionada no estado local.
  */

  onCloseImportarXmls() {
    SpedImportTxtStore.obs.importarTxts = false
    this.obs.selectedEmpresaImport = newEmpresa()
  }

  /**
   * Formata uma data/hora adicionando 3 horas e retornando no padrão "DD/MM/YYYY às HH:mm".
   *
   * Utiliza a biblioteca `moment.js` para manipulação e formatação da data recebida em `cell`.
   * A função soma 3 horas antes de formatar, o que pode ser usado para ajuste de fuso horário
   * ou compensação de diferença entre backend e frontend.
   *
   * O parâmetro `row` é recebido pela assinatura (comum em callbacks de tabelas), mas não é
   * utilizado no processamento atual.
   *
   * @function dateFormatter
   * @param {string|Date|number} cell - Valor da data original a ser formatado. Pode ser
   *   uma string, objeto `Date` ou timestamp aceito pelo `moment`.
   * @param {any} row - Linha/registro associado ao valor (não utilizado nesta implementação).
   * @returns {string} Data e hora formatadas no padrão "DD/MM/YYYY ás HH:mm".
  */

  dateFormatter(cell, row) {
    return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " ás " + moment(cell).add(3, "hour").format("HH:mm");
  };

  /**
   * Formata a exibição do CNPJ na tabela combinando razão social e CNPJ formatado.
   *
   * Verifica se a linha (`row`) possui `cnpj` e, em seguida, busca a empresa correspondente
   * no `EmpresaStore` por meio de `getByCnpj`. Caso a empresa seja encontrada, retorna uma
   * string no formato:
   *
   * `"{razao_social_truncada} - {cnpj_formatado}"`
   *
   * A razão social é limitada aos primeiros 30 caracteres para melhor exibição em tabelas
   * ou colunas com espaço reduzido.
   *
   * Se não houver CNPJ na linha ou a empresa não for encontrada no store, retorna string vazia.
   *
   * @function cnpjFormatter
   * @param {any} cell - Valor da célula (não utilizado diretamente nesta implementação).
   * @param {Object} row - Registro da linha da tabela contendo, entre outros campos, o `cnpj`.
   * @param {string} [row.cnpj] - CNPJ da empresa associado à linha.
   * @returns {string} Texto formatado com razão social truncada e CNPJ formatado, ou string
   *   vazia quando não houver dados suficientes para exibição.
  */

  cnpjFormatter(cell, row) {
    if (row.cnpj) {
      let emp = EmpresaStore.getByCnpj(row.cnpj);
      if (emp) {
        return `${emp.razao_social.substring(0, 30)} - ${formatCnpj(row.cnpj)}`
      }
    }

    return ""
  };

  /**
   * Seleciona uma empresa para filtro e dispara a consulta de tarefas SPED Excel.
   *
   * Atualiza o estado local `this.obs.selectEmpresa` com a empresa recebida e, em seguida,
   * executa a consulta da primeira página (`1`) no `SpedImportExcelStore`, utilizando o
   * CNPJ da empresa selecionada como filtro.
   *
   * O uso de `await` garante que a chamada de consulta seja concluída antes do término
   * da função, o que pode ser útil para sincronizar fluxos dependentes da atualização.
   *
   * @async
   * @function handleBuscaTarefas
   * @param {Object} empresa - Empresa selecionada para filtrar a listagem de tarefas.
   * @param {string} empresa.cnpj - CNPJ da empresa utilizado na consulta.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é atualizar
   *   a empresa selecionada no estado local e recarregar a listagem filtrada pela empresa.
  */

  handleBuscaTarefas = async (empresa) => {

    this.obs.selectEmpresa = empresa

    await SpedImportTxtStore.handleConsulta(1, this.obs.selectEmpresa.cnpj);

  };

  /**
   * Atualiza a página atual da listagem e dispara nova consulta de tarefas SPED Excel.
   *
   * Define o número da página selecionada em `this.obs.current_page` e, em seguida,
   * executa a consulta no `SpedImportExcelStore` utilizando a página informada e o
   * CNPJ atualmente selecionado em `this.obs.selectEmpresa.cnpj`.
   *
   * O uso de `await` garante que a atualização da listagem seja concluída antes do
   * término da função, o que pode ser útil em fluxos de paginação com dependência
   * de estado ou feedback visual.
   *
   * @async
   * @function handleChangePage
   * @param {number} page - Número da página a ser carregada na listagem.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é atualizar
   *   a página atual no estado local e recarregar a listagem paginada.
  */

  handleChangePage = async (page) => {

    this.obs.current_page = page

    await SpedImportTxtStore.handleConsulta(page, this.obs.selectEmpresa.cnpj);

  };

  /**
   * Formata a exibição do status da tarefa/relatório utilizando uma tag visual.
   *
   * Utiliza a função `getTag` para retornar a representação visual do status
   * presente em `row.status`, aplicando o contexto/tipo `"calculo"`.
   * Esse formatter é normalmente usado em colunas de tabela para padronizar
   * a apresentação de status (ex.: pendente, processando, concluído, erro).
   *
   * O parâmetro `cell` é recebido pela assinatura (comum em callbacks de tabelas),
   * mas não é utilizado no processamento atual.
   *
   * @function statusFormatter
   * @param {any} cell - Valor da célula (não utilizado diretamente nesta implementação).
   * @param {Object} row - Registro da linha da tabela que contém o status.
   * @param {string|number} row.status - Status da tarefa/relatório utilizado para gerar a tag.
   * @returns {any} Tag/elemento retornado por `getTag` para renderização do status.
  */

  statusFormatter(cell, row) {

    return getTag(row.status, "calculo");

  }

  /**
   * Processa o arquivo Excel enviado por drag-and-drop, valida permissão e inicia a importação.
   *
   * Obtém os níveis de acesso do usuário atual a partir do `AppStore` e verifica se possui
   * a permissão `"i1"` para importar arquivo SPED. Caso não possua, exibe uma mensagem de
   * aviso e encerra a execução sem prosseguir.
   *
   * Quando autorizado, cria um `FormData`, adiciona o arquivo enviado (`files[0]`) e o CNPJ
   * da empresa selecionada para importação (`this.obs.selectedEmpresaImport.cnpj`), e então
   * chama `SpedImportExcelStore.handldeCreateTaskImportExcel(data)` para enviar os dados à API.
   *
   * Após a importação, redefine a página atual para `1` e dispara uma nova consulta da listagem
   * para atualizar os dados exibidos na tela.
   *
   * @async
   * @function handleFileDropEntradaExcel
   * @param {any} files - Coleção/lista de arquivos recebida no evento de drop. Espera-se
   *   que o primeiro item (`files[0]`) seja o arquivo Excel a ser importado.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é validar
   *   permissão, enviar o arquivo para importação e atualizar a listagem de tarefas.
  */

  handleFileDropEntrada = async (files: any) => {
    let niveis_acesso = AppStore.obs1.usuario ? AppStore.obs1.usuario.niveis_acesso : [""];

    if (niveis_acesso.indexOf("i1") == -1) {
      toast.warning("Você não possui permissão para importar arquivo SPED", {});
      return
    }

    if (this.obs.selectedEmpresaImport.razao_social == "") {
      toast.warning("Você precisa selecionar uma Empresa", {});
      return
    }

    var data = new FormData();

    data.append("file", files[0])
    data.append("cnpj", this.obs.selectedEmpresaImport.cnpj)

    await SpedImportTxtStore.handldeCreateTaskImport(data);

    this.obs.current_page = 1;

    SpedImportTxtStore.handleConsulta(1, this.obs.selectEmpresa.cnpj);

  };

  /**
   * Trata arquivos rejeitados no upload de Excel, exibindo mensagens de validação ao usuário.
   *
   * Verifica se mais de um arquivo foi enviado no evento de rejeição. Nesse caso,
   * exibe um aviso informando que apenas um arquivo por vez é permitido para importação.
   *
   * Se a rejeição não for por quantidade de arquivos, exibe uma mensagem indicando que
   * o formato é inválido e que apenas arquivos com extensão `.xlsx` são aceitos.
   *
   * Embora seja uma função assíncrona, não há operações `await` no fluxo atual.
   *
   * @async
   * @function handleFileDropRejectedExcel
   * @param {any} files - Coleção/lista de arquivos rejeitados no upload.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é exibir
   *   mensagens de aviso ao usuário sobre quantidade ou formato inválido de arquivo.
  */
 
  handleFileDropRejected = async (files: any) => {
    if (files.length > 1) {
      toast.warning("Apenas um arquivo por vez é liberado para importação.", {});
      return;
    }

    toast.warning("Arquivo inválido, é aceito somente arquivos .txt", {});
    return;
  };

  render() {
    function fileFormatter(cell, row) {
      return (
        <>
          <MDBBtn
            color="default"
            rounded
            size="sm"
            disabled={row.status != 2}
            onClick={() => SpedImportTxtStore.handleDownloadArquivoRelatorio(row.relatorio_id, row.cnpj, row.format)}
          >
            <img src="/images/download.svg" />
          </MDBBtn>
        </>
      );
    }

    function buttonFormatterDetalhes(cell, row) {
      return (
        <>
          <MDBBtn disabled={row.status != 3} color="default" rounded size="sm" onClick={(e) => handleViewDetalhes(row)} >
            <img src="/images/view.png" width="18px" />
          </MDBBtn>
        </>
      );
    }

    function dateFormatter(cell, row) {
      return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " ás " + moment(cell).add(3, "hour").format("HH:mm");
    };

    function cnpjFormatter(cell, row) {
      if (row.cnpj) {
        let emp = EmpresaStore.getByCnpj(row.cnpj);
        if (emp) {
          return `${emp.razao_social.substring(0, 30)} - ${formatCnpj(row.cnpj)}`
        }
      }

      return ""
    };

    function statusFormatter(cell, row) {

      return getTag(row.status, "calculo");

    }

    const handleViewDetalhes = async (row) => {

      this.obs.tarefa = row

      this.obs.open_detalhes = true

    };

    let empresaTextImport = this.obs.selectedEmpresaImport.razao_social != ""
      ? `${this.obs.selectedEmpresaImport.razao_social.substring(0, 38)}... (${formatCnpj(this.obs.selectedEmpresaImport.cnpj)})`
      : "Selecione uma empresa";

    let empresaText = this.obs.selectEmpresa.cnpj != ""
      ? `${this.obs.selectEmpresa.razao_social.substring(0, 38)}... (${formatCnpj(this.obs.selectEmpresa.cnpj)})`
      : "Selecione uma empresa";

    return (
      <>
        <MDBRow>
          <MDBCol md="4">
            <FormGroup className="empresaSelect" label="Empresa">
              <EmpresaSelect
                items={EmpresaStore.sortedEmpresas}
                itemRenderer={renderEmpresa}
                itemPredicate={filterEmpresa}
                onItemSelect={(e: IEmpresa) => this.handleBuscaTarefas(e)}
                inputProps={{ placeholder: "Filtrar uma empresa" }}
              >
                <Button
                  text={empresaText || "Selecione uma empresa"}
                  alignText={Alignment.LEFT}
                  rightIcon={
                    this.obs.selectEmpresa.cnpj ? (
                      <Icon
                        className="clear-icon-empresa"
                        icon="cross"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          this.handleBuscaTarefas(newEmpresa());
                        }}
                      />
                    ) : (
                      <Icon icon="double-caret-vertical" style={{ padding: 5 }} />
                    )
                  }
                />
              </EmpresaSelect>
            </FormGroup>
          </MDBCol>

          <MDBCol md="2">
            <MDBBtn className="btnDefault btnBuscarNfe mt25" onClick={() => this.handleChangePage(1)}>
              Buscar
            </MDBBtn>
          </MDBCol>

        </MDBRow>
        <MDBRow>
          <MDBCol md="12">
            <MDBBtn
              className="btnDefault btnImportarXmls"
              onClick={() => {
                this.obs.selectedEmpresaImport = newEmpresa();
                SpedImportTxtStore.obs.importarTxts = true;
              }}
              style={{ float: "left" }}
              data-tut="importar_xmls"
            >
              Converter TXT para Excel FIPO
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol md="12">
            <BootstrapTable className="TableDefaultImport" data={SpedImportTxtStore.obs.tasks_sped} style={{ marginTop: 10 }}>
              <TableHeaderColumn dataField="_id" isKey={true} hidden width="1%"> # </TableHeaderColumn>
              <TableHeaderColumn dataFormat={dateFormatter} dataField="inserted_at" width="20%">Data</TableHeaderColumn>
              <TableHeaderColumn dataFormat={cnpjFormatter} width="30%">Empresa</TableHeaderColumn>
              <TableHeaderColumn dataFormat={statusFormatter} width="25%">Status</TableHeaderColumn>
              <TableHeaderColumn dataFormat={fileFormatter} width="10%">Arquivo</TableHeaderColumn>
              <TableHeaderColumn dataFormat={buttonFormatterDetalhes} width="10%">Detalhes</TableHeaderColumn>
            </BootstrapTable>
          </MDBCol>
        </MDBRow>

        <Alert
          confirmButtonText="Fechar"
          isOpen={SpedImportTxtStore.obs.importarTxts}
          onClose={() => this.onCloseImportarXmls()}
          canOutsideClickCancel={true}
        >
          <h2 className="titleSEFAZ">Conversão TXT para Excel FIPO</h2>
          <MDBRow>
            <MDBCol md="12">
              <FormGroup className="empresaSelectImport" label="Empresa">
                <EmpresaSelect
                  items={EmpresaStore.sortedEmpresas}
                  itemRenderer={renderEmpresa}
                  itemPredicate={filterEmpresa}
                  onItemSelect={(e: IEmpresa) => this.obs.selectedEmpresaImport = e}
                  inputProps={{ placeholder: "Filtrar uma empresa" }}
                >
                  <Button
                    text={empresaTextImport}
                    rightIcon="double-caret-vertical"
                    alignText={Alignment.LEFT}
                  />
                </EmpresaSelect>
              </FormGroup>
            </MDBCol>

          </MDBRow>

          {this.obs.selectedEmpresaImport.razao_social == "" ?
            <div>
              <NonIdealState
                icon="box"
                title="Selecione a empresa para a qual deseja realizar a importação."
              />
            </div>
            :
            <div>
              <Dropzone
                accept={{
                  "files/txt": [".txt"],
                }}
                maxFiles={1}
                multiple={false}
                onDropAccepted={this.handleFileDropEntrada}
                onDropRejected={this.handleFileDropRejected}
              >
                {({ getRootProps, getInputProps }) => (
                  <>
                    <div {...getRootProps()} style={{ marginBottom: 20 }}>
                      <input {...getInputProps()} />
                      <Button
                        icon="upload"
                        text="Selecionar SPED para upload"
                        className="btn-file btnDefault noMargin"
                      />
                    </div>

                    <div {...getRootProps()} className="nz-upload-box">
                      <input {...getInputProps()} />
                      <Icon icon="upload" iconSize={60} />
                      <span style={{ marginLeft: 20 }}>
                        Arraste o arquivo para importar.{" "}
                      </span>
                    </div>
                  </>
                )}
              </Dropzone>
            </div>

          }

        </Alert>

        <Alert
          confirmButtonText="Fechar"
          isOpen={this.obs.open_detalhes}
          onClose={() => this.obs.open_detalhes = false}
          onConfirm={() => this.obs.open_detalhes = false}
          canOutsideClickCancel={true}
          className="AlertErrorSped"
        >
          <h2 className="titleSEFAZ">Erros</h2>
          <MDBCol md="12">
            {this.obs.tarefa.error &&
              <div className="errosSped">
                <BootstrapTable className="nomeArquivos" data={[this.obs.tarefa]} hover>
                  <TableHeaderColumn dataField='id' isKey={true} hidden width='1%'>#</TableHeaderColumn>
                  <TableHeaderColumn dataField='error' width='100%'></TableHeaderColumn>
                </BootstrapTable>
              </div>
            }
          </MDBCol>
        </Alert>

        <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
          <Pager
            currentPage={this.obs.current_page}
            totalCount={SpedImportTxtStore.obs.length_tasks_sped}
            pageSize={15}
            onPageChange={(page) =>
              this.handleChangePage((page))
            }
          />
        </div>

        <LoadingModal open={SpedImportTxtStore.obs.loadingImport} onClose={() => SpedImportTxtStore.obs.loadingImport = false} title={"Importando arquivo"} />
        <LoadingModal open={SpedImportTxtStore.obs.loading} onClose={() => SpedImportTxtStore.obs.loading = false} title={"Baixando arquivo"} />

      </>

    )
  }
}

export default SpedPlanilhaPage;
